import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Alert,
} from '@mui/material';
import { Visibility as ViewIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Tooltip, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface PrescriptionItem {
  drug_name?: string;
  medication_name?: string;
  quantity?: number;
  dosage?: string;
  instructions?: string;
}

interface Prescription {
  id: string;
  prescription_number?: string;
  pet_name: string;
  vet_name?: string;
  created_at?: string;
  prescribed_date?: string;
  status: string;
  items?: PrescriptionItem[];
  item_count?: number;
}

const statusColor = (status: string): 'warning' | 'success' | 'error' | 'default' => {
  switch (status?.toLowerCase()) {
    case 'pending_fulfillment':
    case 'pending': return 'warning';
    case 'dispensed': return 'success';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

const statusLabel = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending_fulfillment': return 'Pending Fulfillment';
    case 'dispensed': return 'Dispensed';
    case 'cancelled': return 'Cancelled';
    default: return status;
  }
};

const PrescriptionsPage = () => {
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewPrescription, setViewPrescription] = useState<Prescription | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editRx, setEditRx] = useState<Prescription | null>(null);
  const [editForm, setEditForm] = useState({ status: '', clinical_notes: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteRx, setDeleteRx] = useState<Prescription | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  const fetchPrescriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(API_ENDPOINTS.PRESCRIPTIONS.LIST);
      setPrescriptions(res.data || res || []);
    } catch (err) {
      console.error('Failed to fetch prescriptions:', err);
      setError('Failed to load prescriptions.');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (prescription: Prescription) => {
    setDetailLoading(true);
    setViewPrescription(prescription);
    try {
      const res = await api.get(API_ENDPOINTS.PRESCRIPTIONS.GET(prescription.id));
      setViewPrescription(res.data || res);
    } catch {
      // keep existing data
    } finally {
      setDetailLoading(false);
    }
  };

  const openEdit = (rx: Prescription) => {
    setEditRx(rx);
    setEditForm({ status: rx.status, clinical_notes: '' });
  };

  const handleEdit = async () => {
    if (!editRx) return;
    setEditSaving(true);
    try {
      await api.put(`/prescriptions/${editRx.id}`, editForm);
      setEditRx(null);
      fetchPrescriptions();
    } catch { /* ignore */ } finally { setEditSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteRx) return;
    setDeleting(true);
    try {
      await api.delete(`/prescriptions/${deleteRx.id}`);
      setDeleteRx(null);
      fetchPrescriptions();
    } catch { /* ignore */ } finally { setDeleting(false); }
  };

  const filtered = prescriptions.filter((p) =>
    `${p.pet_name} ${p.prescription_number || ''} ${p.vet_name || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Prescriptions</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by patient, vet..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3 }}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rx #</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Veterinarian</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No prescriptions found</TableCell>
              </TableRow>
            ) : (
              filtered.map((rx) => (
                <TableRow key={rx.id} hover>
                  <TableCell>{rx.prescription_number || `#${rx.id}`}</TableCell>
                  <TableCell>{rx.pet_name}</TableCell>
                  <TableCell>{rx.vet_name || '—'}</TableCell>
                  <TableCell>
                    {new Date(rx.created_at || rx.prescribed_date || Date.now()).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{rx.item_count ?? rx.items?.length ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabel(rx.status)}
                      color={statusColor(rx.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View"><IconButton size="small" onClick={() => openDetail(rx)}><ViewIcon /></IconButton></Tooltip>
                    {rx.status !== 'Dispensed' && rx.status !== 'dispensed' && (
                      <>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(rx)}><EditIcon /></IconButton></Tooltip>
                        <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={() => setDeleteRx(rx)}><DeleteIcon /></IconButton></Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Prescription Detail Dialog */}
      <Dialog open={!!viewPrescription} onClose={() => setViewPrescription(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Prescription {viewPrescription?.prescription_number || `#${viewPrescription?.id}`}
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
          ) : viewPrescription && (
            <Box>
              <Typography><strong>Patient:</strong> {viewPrescription.pet_name}</Typography>
              <Typography><strong>Veterinarian:</strong> {viewPrescription.vet_name || '—'}</Typography>
              <Typography>
                <strong>Date:</strong> {new Date(viewPrescription.created_at || viewPrescription.prescribed_date || Date.now()).toLocaleDateString()}
              </Typography>
              <Typography>
                <strong>Status:</strong>{' '}
                <Chip label={statusLabel(viewPrescription.status)} color={statusColor(viewPrescription.status)} size="small" />
              </Typography>

              {viewPrescription.items && viewPrescription.items.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>Medications</Typography>
                  {viewPrescription.items.map((item, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {item.drug_name || item.medication_name || 'Unknown medication'}
                      </Typography>
                      {item.dosage && <Typography variant="body2">Dosage: {item.dosage}</Typography>}
                      {item.quantity != null && <Typography variant="body2">Qty: {item.quantity}</Typography>}
                      {item.instructions && <Typography variant="body2">Instructions: {item.instructions}</Typography>}
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewPrescription(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Edit dialog */}
      <Dialog open={!!editRx} onClose={() => setEditRx(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Prescription</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select value={editForm.status} label="Status" onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Clinical Notes" multiline rows={3}
            value={editForm.clinical_notes} onChange={e => setEditForm(f => ({ ...f, clinical_notes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRx(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editSaving}>{editSaving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteRx} onClose={() => setDeleteRx(null)}>
        <DialogTitle>Cancel Prescription</DialogTitle>
        <DialogContent>
          <Typography>Cancel prescription {deleteRx?.prescription_number || `#${deleteRx?.id}`} for <strong>{deleteRx?.pet_name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRx(null)}>Keep</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>{deleting ? 'Cancelling...' : 'Cancel Prescription'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrescriptionsPage;
